import { inject, type InjectionKey } from "vue";
import { useNuxtApp } from "nuxt/app";

import type { FModalAuditoriaVolumeDun14, DadosValidacaoDun14 } from "~/classes/auditoriaVolumesCarga/FModalAuditoriaVolumeDun14";
import type { ValidarDun14ResponseDTO } from "@r3backend/src/modules/PROC_AVERIGUACAO_PICKING_CX_DUN14/app/dto/validarDun14.dto";
import type { ValidarPesoVariavelResponseDTO } from "@r3backend/src/modules/PROC_AVERIGUACAO_PICKING_CX_DUN14/app/dto/validarPesoVariavel.dto";

/**
 * Chave de injeção para a instância de `FModalAuditoriaVolumeDun14`.
 *
 * O **pai** (página ou componente que cria a instância) faz:
 * ```typescript
 * const fDun14 = new FModalAuditoriaVolumeDun14();
 * provide(AUDITORIA_VOLUME_DUN14_KEY, fDun14);
 * ```
 *
 * O **filho** (`ModalAuditoriaVolumeDun14.vue`) recupera via:
 * ```typescript
 * const { instancia } = useAuditoriaVolumeDun14();
 * ```
 */
export const AUDITORIA_VOLUME_DUN14_KEY: InjectionKey<FModalAuditoriaVolumeDun14> =
	Symbol("FModalAuditoriaVolumeDun14");

/**
 * Composable de acesso e lógica de negócio para `FModalAuditoriaVolumeDun14`.
 *
 * **Não cria nenhuma instância internamente.**
 * A instância deve ser criada no `<script setup>` do componente/página que a usa
 * e passada como parâmetro ou fornecida via `provide`.
 *
 * **Uso em componentes-pai** (passando a instância como parâmetro):
 * ```typescript
 * const fDun14 = new FModalAuditoriaVolumeDun14();
 * const { validarDun14, validarPesoVariavel } = useAuditoriaVolumeDun14(fDun14);
 * ```
 *
 * **Uso em componentes-filho** (recuperando a instância via inject):
 * ```typescript
 * const { instancia, validarDun14 } = useAuditoriaVolumeDun14();
 * ```
 */
export function useAuditoriaVolumeDun14(f?: FModalAuditoriaVolumeDun14) {
	// Quando chamado sem argumento, recupera a instância fornecida pelo pai via provide
	const instancia = f ?? inject(AUDITORIA_VOLUME_DUN14_KEY);

	if (!instancia) {
		throw new Error(
			"[useAuditoriaVolumeDun14] Instância não encontrada. " +
			"Passe a instância como parâmetro ou use provide(AUDITORIA_VOLUME_DUN14_KEY, ...) no componente pai.",
		);
	}

	const { $r3fetchdata } = useNuxtApp();

	// ────────────────────────────────────────────────────────────────────────
	// Validação DUN14/EAN13
	// ────────────────────────────────────────────────────────────────────────

	/**
	 * Valida o código DUN14 ou EAN13 contra o backend.
	 * Sanitiza o prefixo '#' antes de enviar (Regra 2).
	 *
	 * Quando o backend indica `tocarAudio`, sinaliza via `instancia.deveTocarAudio`
	 * para que o `.vue` (único responsável por criar Howl) reaja e toque o som.
	 *
	 * @param empresaId - ID da filial
	 * @param mercadoriaId - ID da mercadoria a validar
	 */
	async function validarDun14(empresaId: number, mercadoriaId: number): Promise<void> {
		const codigoSanitizado = instancia.codigoBipado.value.replace(/^#/, "");

		if (!codigoSanitizado) {
			instancia.mostrarErro("Informe o código de barras.");
			return;
		}

		instancia.isLoading.value = true;
		try {
			const resposta = await $r3fetchdata.post<
				{ response: ValidarDun14ResponseDTO & { tocarAudio?: string } }
			>(
				"/api/procAveriguacaoPickingCxDun14/validarDun14" as any,
				{ empresaId, mercadoriaId, codigoBipado: codigoSanitizado },
			);

			const dados = resposta.response;

			if (!dados.sucesso) {
				// Sinaliza áudio via ref reativo — o .vue assiste e toca com sua instância Howl
				instancia.mostrarErro(dados.mensagem || "Código inválido.", !!dados.tocarAudio);
				instancia.codigoBipado.value = "";
				return;
			}

			instancia.concluirComSucesso({
				sucesso: true,
				codigoBarrasValidado: dados.dados?.codigoValido ?? codigoSanitizado,
				pesoRetornado: null,
				tipoAutomacao: dados.dados?.tipoAutomacao ?? "DUN14",
			});
		} catch (error: any) {
			const isRede = !navigator.onLine || error?.code === "ERR_NETWORK" || error?.message === "Network Error" || error?.message?.includes("Failed to fetch") || error?.status === 0;
			const msg = isRede 
				? "Falha de conexão com o servidor. Verifique sua rede e tente novamente." 
				: (error?.response?.data?.MENSAGEM || error?.message || "Erro ao validar código. Tente novamente.");
			instancia.mostrarErro(msg, isRede ? false : true);
			instancia.codigoBipado.value = "";
		} finally {
			instancia.isLoading.value = false;
		}
	}

	// ────────────────────────────────────────────────────────────────────────
	// Validação de Peso Variável
	// ────────────────────────────────────────────────────────────────────────

	/**
	 * Valida o peso do volume contra os parâmetros logísticos do backend.
	 *
	 * Quando o backend retorna `confirmacaoAtiva: true` (Regra 7), ativa o
	 * sub-diálogo de dupla confirmação — a conclusão ocorre via `confirmarPeso()`.
	 *
	 * @param empresaId - ID da filial
	 * @param mercadoriaId - ID da mercadoria a validar
	 */
	async function validarPesoVariavel(empresaId: number, mercadoriaId: number): Promise<void> {
		if (instancia.pesoInformado.value <= 0) {
			// Peso zero: sinaliza áudio + erro sem round-trip ao backend
			instancia.mostrarErro("Obrigatório informar o peso!", true);
			return;
		}

		instancia.isLoading.value = true;
		try {
			const resposta = await $r3fetchdata.post<
				{ response: ValidarPesoVariavelResponseDTO & { tocarAudio?: string } }
			>(
				"/api/procAveriguacaoPickingCxDun14/validarPesoVariavel" as any,
				{
					empresaId,
					mercadoriaId,
					pesoInformado: instancia.pesoInformado.value,
					codigoBipado: instancia.codigoBipado.value || undefined,
				},
			);

			const dados = resposta.response;

			if (!dados.sucesso) {
				instancia.mostrarErro(dados.mensagem || "Peso inválido.", !!dados.tocarAudio);
				instancia.codigoBipado.value = "";
				instancia.pesoInformado.value = 0;
				instancia.pesoDecodificadoLocal.value = null;
				return;
			}

			// Regra 7: dupla confirmação antes de concluir
			if (dados.dados?.confirmacaoAtiva && dados.dados.faixaTolerancia) {
				instancia.ativarConfirmacaoPeso(dados.dados.faixaTolerancia);
				return;
			}

			instancia.concluirComSucesso({
				sucesso: true,
				codigoBarrasValidado: instancia.codigoBipado.value,
				pesoRetornado: dados.dados?.pesoLiquido ?? instancia.pesoInformado.value,
				tipoAutomacao: "PESO_VARIAVEL",
			});
		} catch (error: any) {
			const isRede = !navigator.onLine || error?.code === "ERR_NETWORK" || error?.message === "Network Error" || error?.message?.includes("Failed to fetch") || error?.status === 0;
			const msg = isRede 
				? "Falha de conexão com o servidor. Verifique sua rede e tente novamente." 
				: (error?.response?.data?.MENSAGEM || error?.message || "Erro ao validar peso. Tente novamente.");
			instancia.mostrarErro(msg, isRede ? false : true);
			instancia.codigoBipado.value = "";
			instancia.pesoInformado.value = 0;
			instancia.pesoDecodificadoLocal.value = null;
		} finally {
			instancia.isLoading.value = false;
		}
	}

	return {
		instancia,
		validarDun14,
		validarPesoVariavel,
	};
}
